import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Plus, MapPin, Clock, Users, DollarSign, Building, Calendar, Briefcase, Eye } from 'lucide-react';
import { getJobPostings, createJobPosting, getJobPostingById } from '../api/recruitment';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { JobPosting } from '../types';

export function JobPostings() {
  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [selectedJob, setSelectedJob] = useState<JobPosting | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const [newJobPosting, setNewJobPosting] = useState({
    title: '',
    department: '',
    location: '',
    type: 'full-time',
    description: '',
    requirements: '',
    experience: '',
    salaryMin: '',
    salaryMax: '',
    deadline: ''
  });

  const canCreateJobPostings = ['admin', 'hr'].includes(user?.role || '');

  useEffect(() => {
    fetchJobPostings();
  }, []);

  const fetchJobPostings = async () => {
    try {
      console.log('Fetching job postings...');
      setLoading(true);
      const response = await getJobPostings();
      console.log('Job postings fetched successfully:', response);
      setJobPostings(response.jobs || []);
    } catch (error: any) {
      console.error('Error fetching job postings:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch job postings",
        variant: "destructive",
      });
      setJobPostings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewJobDetails = async (jobId: string) => {
    try {
      console.log('Fetching job details for ID:', jobId);
      setDetailLoading(true);
      setIsDetailDialogOpen(true);

      const response = await getJobPostingById(jobId);
      console.log('Job details fetched successfully:', response);
      setSelectedJob(response.job);
    } catch (error: any) {
      console.error('Error fetching job details:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch job details",
        variant: "destructive",
      });
      setIsDetailDialogOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreateJobPosting = async () => {
    try {
      console.log('Creating job posting...', newJobPosting);
      setCreateLoading(true);

      // Validate required fields
      if (!newJobPosting.title || !newJobPosting.department || !newJobPosting.location ||
          !newJobPosting.description || !newJobPosting.requirements || !newJobPosting.experience ||
          !newJobPosting.salaryMin || !newJobPosting.salaryMax || !newJobPosting.deadline) {
        toast({
          title: "Error",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }

      // Validate salary range
      const minSalary = parseFloat(newJobPosting.salaryMin);
      const maxSalary = parseFloat(newJobPosting.salaryMax);

      if (isNaN(minSalary) || isNaN(maxSalary) || minSalary <= 0 || maxSalary <= 0) {
        toast({
          title: "Error",
          description: "Please enter valid salary amounts",
          variant: "destructive",
        });
        return;
      }

      if (maxSalary <= minSalary) {
        toast({
          title: "Error",
          description: "Maximum salary must be greater than minimum salary",
          variant: "destructive",
        });
        return;
      }

      // Validate deadline
      const deadline = new Date(newJobPosting.deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (deadline <= today) {
        toast({
          title: "Error",
          description: "Deadline must be in the future",
          variant: "destructive",
        });
        return;
      }

      const jobData = {
        ...newJobPosting,
        requirements: newJobPosting.requirements.split(',').map(req => req.trim()).filter(req => req),
        salary: {
          min: minSalary,
          max: maxSalary
        }
      };

      const response = await createJobPosting(jobData);
      console.log('Job posting created successfully:', response);

      if (response.success) {
        toast({
          title: "Success",
          description: "Job posting created successfully",
        });

        setIsCreateDialogOpen(false);
        setNewJobPosting({
          title: '',
          department: '',
          location: '',
          type: 'full-time',
          description: '',
          requirements: '',
          experience: '',
          salaryMin: '',
          salaryMax: '',
          deadline: ''
        });
        fetchJobPostings();
      }
    } catch (error: any) {
      console.error('Error creating job posting:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create job posting",
        variant: "destructive",
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const formatSalary = (min: number, max: number) => {
    return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'full-time': return 'bg-green-100 text-green-800';
      case 'part-time': return 'bg-blue-100 text-blue-800';
      case 'contract': return 'bg-orange-100 text-orange-800';
      case 'internship': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-red-100 text-red-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Job Postings
          </h1>
          <p className="text-gray-600 mt-1">
            Manage job postings and recruitment
          </p>
        </div>

        {canCreateJobPostings && (
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                <Plus className="h-4 w-4 mr-2" />
                Create New Job Posting
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Job Posting</DialogTitle>
                <DialogDescription>
                  Fill in the details to create a new job posting
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Job Title *</Label>
                    <Input
                      id="title"
                      value={newJobPosting.title}
                      onChange={(e) => setNewJobPosting({...newJobPosting, title: e.target.value})}
                      placeholder="e.g. Senior Developer"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department">Department *</Label>
                    <Input
                      id="department"
                      value={newJobPosting.department}
                      onChange={(e) => setNewJobPosting({...newJobPosting, department: e.target.value})}
                      placeholder="e.g. Engineering"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="location">Location *</Label>
                    <Input
                      id="location"
                      value={newJobPosting.location}
                      onChange={(e) => setNewJobPosting({...newJobPosting, location: e.target.value})}
                      placeholder="e.g. New York, NY"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="type">Employment Type *</Label>
                    <Select value={newJobPosting.type} onValueChange={(value) => setNewJobPosting({...newJobPosting, type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full-time">Full-time</SelectItem>
                        <SelectItem value="part-time">Part-time</SelectItem>
                        <SelectItem value="contract">Contract</SelectItem>
                        <SelectItem value="internship">Internship</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Job Description *</Label>
                  <Textarea
                    id="description"
                    value={newJobPosting.description}
                    onChange={(e) => setNewJobPosting({...newJobPosting, description: e.target.value})}
                    placeholder="Describe the role, responsibilities, and what you're looking for..."
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="requirements">Requirements *</Label>
                  <Textarea
                    id="requirements"
                    value={newJobPosting.requirements}
                    onChange={(e) => setNewJobPosting({...newJobPosting, requirements: e.target.value})}
                    placeholder="Enter requirements separated by commas (e.g. React, JavaScript, TypeScript)"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="experience">Experience Level *</Label>
                  <Select value={newJobPosting.experience} onValueChange={(value) => setNewJobPosting({...newJobPosting, experience: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select experience level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entry">Entry Level (0-2 years)</SelectItem>
                      <SelectItem value="mid">Mid Level (2-5 years)</SelectItem>
                      <SelectItem value="senior">Senior Level (5+ years)</SelectItem>
                      <SelectItem value="lead">Lead/Principal (8+ years)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="salaryMin">Minimum Salary *</Label>
                    <Input
                      id="salaryMin"
                      type="number"
                      value={newJobPosting.salaryMin}
                      onChange={(e) => setNewJobPosting({...newJobPosting, salaryMin: e.target.value})}
                      placeholder="50000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="salaryMax">Maximum Salary *</Label>
                    <Input
                      id="salaryMax"
                      type="number"
                      value={newJobPosting.salaryMax}
                      onChange={(e) => setNewJobPosting({...newJobPosting, salaryMax: e.target.value})}
                      placeholder="80000"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deadline">Application Deadline *</Label>
                  <Input
                    id="deadline"
                    type="date"
                    value={newJobPosting.deadline}
                    onChange={(e) => setNewJobPosting({...newJobPosting, deadline: e.target.value})}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                  disabled={createLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateJobPosting}
                  disabled={createLoading}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                >
                  {createLoading ? 'Creating...' : 'Create Job Posting'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Job Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedJob?.title || 'Job Details'}</DialogTitle>
            <DialogDescription>
              Complete job posting information
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
            </div>
          ) : selectedJob ? (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="flex justify-between items-start">
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold">{selectedJob.title}</h2>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Building className="h-4 w-4" />
                      {selectedJob.department}
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {selectedJob.location}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      Posted {formatDate(selectedJob.postedDate)}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Badge className={getTypeColor(selectedJob.type)}>
                    {selectedJob.type?.charAt(0).toUpperCase() + selectedJob.type.slice(1)}
                  </Badge>
                  <Badge className={getStatusColor(selectedJob.status)}>
                    {selectedJob.status?.charAt(0).toUpperCase() + selectedJob.status.slice(1)}
                  </Badge>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Salary Range</p>
                    <p className="font-medium">{formatSalary(selectedJob.salary.min, selectedJob.salary.max)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Applications</p>
                    <p className="font-medium">{selectedJob.applications || 0}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="text-sm text-gray-600">Deadline</p>
                    <p className="font-medium">{formatDate(selectedJob.deadline)}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Job Description</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedJob.description}</p>
              </div>

              {/* Requirements */}
              {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Requirements</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.requirements.map((requirement, index) => (
                      <Badge key={index} variant="outline">
                        {requirement}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Experience Level</h3>
                <p className="text-gray-700">{selectedJob.experience}</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Job details not available</p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDetailDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Postings List */}
      {jobPostings.length === 0 ? (
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Briefcase className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No job postings found</h3>
            <p className="text-gray-600 text-center mb-4">
              {canCreateJobPostings
                ? "Create your first job posting to start recruiting."
                : "No job postings are currently available."
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {jobPostings.map((job) => (
            <Card key={job._id} className="bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <CardTitle className="text-xl text-gray-900">{job.title}</CardTitle>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Building className="h-4 w-4" />
                        {job.department}
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {job.location}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Posted {formatDate(job.postedDate)}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Badge className={getTypeColor(job.type)}>
                      {job.type?.charAt(0).toUpperCase() + job.type.slice(1)}
                    </Badge>
                    <Badge className={getStatusColor(job.status)}>
                      {job.status?.charAt(0).toUpperCase() + job.status.slice(1)}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <CardDescription className="text-gray-700">
                  {job.description.length > 200
                    ? `${job.description.substring(0, 200)}...`
                    : job.description}
                </CardDescription>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium">
                      {formatSalary(job.salary.min, job.salary.max)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    <span className="text-sm">
                      {job.applications || 0} applications
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-orange-600" />
                    <span className="text-sm">
                      Deadline: {formatDate(job.deadline)}
                    </span>
                  </div>
                </div>

                {job.requirements && job.requirements.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-900">Requirements:</h4>
                    <div className="flex flex-wrap gap-2">
                      {job.requirements.slice(0, 5).map((requirement, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {requirement}
                        </Badge>
                      ))}
                      {job.requirements.length > 5 && (
                        <Badge variant="outline" className="text-xs">
                          +{job.requirements.length - 5} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    Experience: <span className="font-medium">{job.experienceLevel}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleViewJobDetails(job._id)}>
                      <Eye className="h-4 w-4 mr-1" />
                      View Details
                    </Button>
                    {canCreateJobPostings && (
                      <Button variant="outline" size="sm">
                        Edit
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}